import { ACTION_LABELS, type AuditEntryDto, ENTITY_LABELS, type MeDto, type Page, SOURCE_LABELS } from '@bqcrm/shared';
import { Alert, Card, Code, Group, Pagination, Select, Stack, Table, Text } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { can } from '../auth';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState } from '../components/ui';
import { formatDateTime } from '../format';

const PAGE = 50;

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v));

/** Zmiany w formie „pole: przed → po”; pozostałe dane jako JSON. */
export function Changes({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return <Text c="dimmed">—</Text>;
  // pola wewnętrzne (np. _odbiorcy powiadomień) — bez pokazywania
  value = Object.fromEntries(Object.entries(value as Record<string, unknown>).filter(([k]) => !k.startsWith('_')));
  const entries = Object.entries(value as Record<string, unknown>);
  const isDiff = entries.every(([, v]) => Array.isArray(v) && v.length === 2);
  if (!isDiff) return <Code block>{JSON.stringify(value, null, 1)}</Code>;
  return (
    <Stack gap={2}>
      {entries.map(([k, v]) => {
        const [a, b] = v as [unknown, unknown];
        return (
          <Text key={k} size="xs">
            <b>{k}</b>: {show(a)} → {show(b)}
          </Text>
        );
      })}
    </Stack>
  );
}

export function AuditPage({ me }: { me: MeDto }) {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState<string | null>(null);
  const ownOnly = !can(me, 'audyt.podglad', 'W');
  const params = new URLSearchParams({ limit: String(PAGE), offset: String((page - 1) * PAGE) });
  if (entityType) params.set('entityType', entityType);
  const q = useQuery({ queryKey: ['audit', page, entityType], queryFn: () => api<Page<AuditEntryDto>>(`/audit?${params}`) });

  return (
    <>
      <PageHeader
        title="Historia operacji"
        description="Zapis istotnych operacji: logowania, zmiany danych firm, numeracji, pracowników i kont, ról i uprawnień. Historii nie można zmienić ani usunąć — także bezpośrednio w bazie."
        actions={
          <Select
            placeholder="Wszystkie obiekty"
            clearable
            data={Object.entries(ENTITY_LABELS).map(([value, label]) => ({ value, label }))}
            value={entityType}
            onChange={(v) => {
              setEntityType(v);
              setPage(1);
            }}
          />
        }
      />
      {ownOnly && (
        <Alert variant="light" mb="md">
          Widzisz wyłącznie operacje wykonane przez siebie.
        </Alert>
      )}
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kiedy</Table.Th>
                  <Table.Th>Kto</Table.Th>
                  <Table.Th>Operacja</Table.Th>
                  <Table.Th>Obiekt</Table.Th>
                  <Table.Th>Zmiany</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data?.items.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{e.actorName ?? 'system'}</Text>
                      <Text size="xs" c="dimmed">
                        {SOURCE_LABELS[e.source] ?? e.source}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ACTION_LABELS[e.action] ?? e.action}</Text>
                      {e.reason && (
                        <Text size="xs" c="dimmed">
                          Powód: {e.reason}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ENTITY_LABELS[e.entityType] ?? e.entityType}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {e.entityId?.slice(0, 8) ?? ''}
                      </Text>
                    </Table.Td>
                    <Table.Td maw={420}>
                      <Changes value={e.changes} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {q.data && q.data.items.length === 0 && (
            <EmptyState compact icon={<IconHistory size={18} />} title="Brak wpisów" hint={entityType ? 'Dla wybranego rodzaju obiektu nie ma jeszcze operacji — wyczyść filtr.' : 'Operacje pojawią się tu, gdy ktoś coś zmieni.'} />
          )}
          <Group justify="space-between" px="md" py="sm" wrap="wrap" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Text size="sm" c="dimmed">
              Wpisów: {q.data?.total ?? 0}
            </Text>
            <Pagination total={Math.max(1, Math.ceil((q.data?.total ?? 0) / PAGE))} value={page} onChange={setPage} />
          </Group>
        </QueryState>
      </Card>
    </>
  );
}
